/**
 * Authentication Service
 * 
 * Handles all authentication logic with Django backend using session-based authentication.
 * Follows best practices for SPA authentication with Django.
 */

export interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
}

export interface AuthStatus {
  isAuthenticated: boolean;
  user: User | null;
  csrfToken: string | null;
}

export class AuthenticationService {
  private static instance: AuthenticationService;
  private authStatus: AuthStatus = {
    isAuthenticated: false,
    user: null,
    csrfToken: null
  };
  private baseUrl: string;
  private listeners: Array<(status: AuthStatus) => void> = [];

  private constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || this.detectBaseUrl();
  }

  private detectBaseUrl(): string {
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
        && window.location.hostname !== '127.0.0.1') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return 'http://localhost:8000';
  }

  public static getInstance(): AuthenticationService {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = new AuthenticationService();
    }
    return AuthenticationService.instance;
  }

  /**
   * Add a listener for authentication status changes
   */
  public addAuthStatusListener(listener: (status: AuthStatus) => void): void {
    this.listeners.push(listener);
  }

  /**
   * Remove an authentication status listener
   */
  public removeAuthStatusListener(listener: (status: AuthStatus) => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Notify all listeners of authentication status changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.authStatus));
  }

  /**
   * Get current authentication status (from memory)
   */
  public getAuthStatus(): AuthStatus {
    return { ...this.authStatus };
  }

  /**
   * Check if user is currently authenticated (from memory)
   */
  public isAuthenticated(): boolean {
    return this.authStatus.isAuthenticated;
  }

  /**
   * Get current user (from memory)
   */
  public getCurrentUser(): User | null {
    return this.authStatus.user;
  }

  /**
   * Get CSRF token for making authenticated requests
   */
  public getCsrfToken(): string | null {
    return this.authStatus.csrfToken;
  }

  /**
   * Check authentication status with the backend
   * This is the single source of truth for authentication
   */
  public async checkAuthStatus(): Promise<AuthStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/status/`, {
        method: 'GET',
        credentials: 'include', // Include session cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        this.authStatus = {
          isAuthenticated: data.authenticated,
          user: data.user || null,
          csrfToken: data.csrf_token || null
        };
      } else {
        // Not authenticated or error
        this.authStatus = {
          isAuthenticated: false,
          user: null,
          csrfToken: null
        };
      }
    } catch (error) {
      console.error('Failed to check authentication status:', error);
      this.authStatus = {
        isAuthenticated: false,
        user: null,
        csrfToken: null
      };
    }

    this.notifyListeners();
    return { ...this.authStatus };
  }

  /**
   * Login with username and password
   */
  public async login(username: string, password: string): Promise<boolean> {
    try {
      // First get CSRF token
      await this.getCsrfTokenFromServer();

      const response = await fetch(`${this.baseUrl}/api/auth/login/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.authStatus.csrfToken || '',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        // Login successful, check auth status to get user info
        await this.checkAuthStatus();
        return true;
      } else {
        console.error('Login failed:', response.status, response.statusText);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }

  /**
   * Logout from the backend
   */
  public async logout(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.authStatus.csrfToken || '',
        },
      });

      // Clear auth status regardless of response
      this.authStatus = {
        isAuthenticated: false,
        user: null,
        csrfToken: null
      };
      this.notifyListeners();

      return response.ok;
    } catch (error) {
      console.error('Logout error:', error);
      // Clear auth status even on error
      this.authStatus = {
        isAuthenticated: false,
        user: null,
        csrfToken: null
      };
      this.notifyListeners();
      return false;
    }
  }

  /**
   * Get CSRF token from server
   */
  private async getCsrfTokenFromServer(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/csrf/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        this.authStatus.csrfToken = data.csrf_token;
      }
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
    }
  }

  /**
   * Make an authenticated API request
   * This helper ensures all requests include proper authentication headers
   */
  public async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = new Headers(options.headers);
    
    // Always include credentials for session authentication
    const requestOptions: RequestInit = {
      ...options,
      credentials: 'include',
      headers,
    };

    // Add CSRF token for non-GET requests
    if (options.method && options.method !== 'GET' && this.authStatus.csrfToken) {
      headers.set('X-CSRFToken', this.authStatus.csrfToken);
    }

    const response = await fetch(url, requestOptions);

    // If we get a 401, the session might have expired
    if (response.status === 401) {
      console.log('Received 401, checking auth status...');
      await this.checkAuthStatus();
    }

    return response;
  }

  /**
   * Initialize the authentication service
   * Should be called when the app starts
   */
  public async initialize(): Promise<void> {
    console.log('🔐 Initializing authentication service...');
    await this.checkAuthStatus();
    console.log('🔐 Authentication service initialized:', this.authStatus.isAuthenticated ? 'authenticated' : 'not authenticated');
  }
}

// Export singleton instance
export const authService = AuthenticationService.getInstance();
